// -------------------------------------------------------------
// Unit-aware pricing resolver (mirrors backend resolve_unit_price)
// -------------------------------------------------------------

function pickEffectiveUnit(product, customerType) {
    if (customerType === 'wholesale' && product?.wholesale_unit_code) return product.wholesale_unit_code;
    return product?.retail_unit_code || product?.wholesale_unit_code || 'pcs';
}

function pickMatrixRow(rows, unitCode, customerType, level) {
    if (!rows || rows.length === 0) return null;
    if (unitCode) {
        for (const lv of [level, 0]) {
            const r = rows.find(x => x.unit_code === unitCode && x.customer_type === customerType && Number(x.level || 0) === lv);
            if (r) return r;
        }
        // fallback to retail row for same unit
        const retailFallback = rows.find(x => x.unit_code === unitCode && x.customer_type === 'retail' && Number(x.level || 0) === 0);
        if (retailFallback) return retailFallback;
    }
    for (const lv of [level, 0]) {
        const r = rows.find(x => x.customer_type === customerType && Number(x.level || 0) === lv);
        if (r) return r;
    }
    return rows[0];
}

/** Legacy path: convert the old flat pricing block into a shape resembling the matrix row. */
function resolveLegacy(product, customerType, level) {
    const p = product?.pricing || {};
    if (customerType === 'wholesale') {
        if (level >= 1 && level <= 5) {
            const lv = p[`level_${level}_price`];
            if (lv != null) return { selling_price: Number(lv), mrp: Number(p.wholesale_mrp || 0), source: 'legacy' };
        }
        if (p.wholesale_selling_price != null) return { selling_price: Number(p.wholesale_selling_price), mrp: Number(p.wholesale_mrp || 0), source: 'legacy' };
    }
    return { selling_price: Number(p.retail_selling_price || 0), mrp: Number(p.retail_mrp || 0), source: 'legacy' };
}

/**
 * Full-shape resolver. Returns { unit_code, price, mrp, source }.
 */
export function resolveProductUnitPrice(product, customerType = 'retail', level = 0, unitCode = null) {
    const unit = unitCode || pickEffectiveUnit(product, customerType);
    const rows = product?.unit_prices || [];
    const row = pickMatrixRow(rows, unit, customerType, Number(level || 0));
    if (row && (Number(row.selling_price) > 0 || Number(row.mrp) > 0)) {
        return {
            unit_code: row.unit_code || unit,
            price: Number(row.selling_price || 0),
            mrp: Number(row.mrp || 0),
            source: 'matrix',
        };
    }
    const leg = resolveLegacy(product, customerType, Number(level || 0));
    return { unit_code: unit, price: leg.selling_price, mrp: leg.mrp, source: leg.source };
}

// Backward-compat helpers used across the storefront
export function resolveProductPrice(product, customerType, level) {
    return resolveProductUnitPrice(product, customerType || 'retail', level || 0).price;
}
export function getMrp(product, customerType) {
    return resolveProductUnitPrice(product, customerType || 'retail', 0).mrp;
}

export function formatInr(n) {
    if (n == null) return '₹0';
    return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// Get a short symbol/label for a unit code (falls back to code itself)
let _unitCache = null; let _unitPending = null;
export async function loadUnitsCached(api) {
    if (_unitCache) return _unitCache;
    if (_unitPending) return _unitPending;
    _unitPending = api.get('/units', { params: { active: true } })
        .then(r => { _unitCache = r.data || []; return _unitCache; })
        .catch(() => []).finally(() => { _unitPending = null; });
    return _unitPending;
}
export function unitSymbolFrom(units, code) {
    if (!code) return '';
    const u = (units || []).find(x => x.code === code);
    return (u?.symbol || code);
}
